import {HttpClient} from '@angular/common/http';
import {inject, Injectable, OnDestroy, signal} from '@angular/core';
import {catchError, forkJoin, map, Observable, of, ReplaySubject, retry, Subject, switchMap, tap, timeout} from 'rxjs';
import {environment} from '@/environments/environment';
import moment from 'moment';
import {APIData} from '../model/common';
import {parseLabel} from '../shared/utils';
import {UserService} from './user.service';
import {Product, User} from '@/app/model';
import {EventNodeAPI} from '../model/interface/event-node';
import {Product as ProductChart} from '../model/product';
import {Registration, RegistrationType} from '../model/interface/registration';
import {Event, EventAPI, KeyPerformanceMetric} from '../model/interface/event';
import {sortBy} from "lodash-es";
import {MqttConnectionService} from "@/app/services/mqtt-connection.service";

@Injectable({
    providedIn: 'root'
})
export class EventService implements OnDestroy {
    public selectedEventNodeSource = signal<EventNodeAPI | null>(null);
    public selectedEvent = signal<Event | null>(null);
    public setLoadEvents = signal<boolean>(true);
    public events$ = new ReplaySubject<Event[]>(1);
    public eventUpdate$ = new ReplaySubject<Event>();
    public eventNodeUpdate$ = new Subject<EventNodeAPI>();

    private nodesByEventId = new Map<string, EventNodeAPI[]>();
    private productsMap = new Map<string, Product>();
    private eventsAPI: EventAPI[] = [];
    private events: Event[] = [];
    private pollingTimeout: any;
    private pollingStarted = false;

    http: HttpClient = inject(HttpClient);

    constructor(
        private userService: UserService,
        private mqttConnectionService: MqttConnectionService
    ) {
        this.user = this.userService.user();
    }

    public emsUrl = environment.emsUrl;
    public user: User | undefined;

    /**
     * Sidebar UI state (in-memory only).
     * Persists across polling and MQTT updates, but resets on navigation/full reload.
     */
    private readonly sidebarSectionsOpen = signal<{ current: boolean; upcoming: boolean; completed: boolean }>({
        current: false,
        upcoming: false,
        completed: false
    });

    /**
     * Tracks whether a section has been touched (either manually toggled or auto-expanded).
     * Used to prevent auto-collapsing sections when selecting events in different sections.
     */
    private readonly sidebarSectionsTouched = signal<{ current: boolean; upcoming: boolean; completed: boolean }>({
        current: false,
        upcoming: false,
        completed: false
    });

    private readonly sidebarExpandedEventIds = signal<Set<string>>(new Set());

    /**
     * Tracks whether a user has explicitly toggled Show/Hide Sites for an event.
     * Used to allow auto-expand on initial load, but keep the card closed once the user closes it.
     */
    private readonly sidebarEventExpansionTouched = signal<Set<string>>(new Set());

    getSidebarSectionsOpen(): { current: boolean; upcoming: boolean; completed: boolean } {
        return this.sidebarSectionsOpen();
    }

    getSidebarSectionsTouched(): { current: boolean; upcoming: boolean; completed: boolean } {
        return this.sidebarSectionsTouched();
    }

    toggleSidebarSection(section: 'current' | 'upcoming' | 'completed'): void {
        this.sidebarSectionsOpen.update(prev => ({ ...prev, [section]: !prev[section] }));
        this.sidebarSectionsTouched.update(prev => ({ ...prev, [section]: true }));
    }

    setSidebarSectionOpen(section: 'current' | 'upcoming' | 'completed', open: boolean): void {
        this.sidebarSectionsOpen.update(prev => ({ ...prev, [section]: open }));
    }

    markSidebarSectionTouched(section: 'current' | 'upcoming' | 'completed'): void {
        this.sidebarSectionsTouched.update(prev => ({ ...prev, [section]: true }));
    }

    isSidebarEventExpanded(eventId: string): boolean {
        if (!eventId) return false;
        return this.sidebarExpandedEventIds().has(eventId);
    }

    setSidebarEventExpanded(eventId: string, expanded: boolean): void {
        if (!eventId) return;
        this.sidebarExpandedEventIds.update(prev => {
            const next = new Set(prev);
            if (expanded) next.add(eventId);
            else next.delete(eventId);
            return next;
        });
    }

    hasSidebarEventExpansionBeenTouched(eventId: string): boolean {
        if (!eventId) return false;
        return this.sidebarEventExpansionTouched().has(eventId);
    }

    markSidebarEventExpansionTouched(eventId: string): void {
        if (!eventId) return;
        this.sidebarEventExpansionTouched.update(prev => {
            const next = new Set(prev);
            next.add(eventId);
            return next;
        });
    }

    private pruneSidebarExpandedEvents(currentEventIds: Set<string>): void {
        this.sidebarExpandedEventIds.update(prev => {
            if (!prev.size) return prev;
            const next = new Set<string>();
            for (const id of prev) {
                if (currentEventIds.has(id)) next.add(id);
            }
            return next;
        });

        this.sidebarEventExpansionTouched.update(prev => {
            if (!prev.size) return prev;
            const next = new Set<string>();
            for (const id of prev) {
                if (currentEventIds.has(id)) next.add(id);
            }
            return next;
        });
    }

    startPolling(): void {
        if (this.pollingStarted) return;
        this.pollingStarted = true;
        this.pollEvents();
    }

    stopPolling(): void {
        this.pollingStarted = false;
        if (this.pollingTimeout) {
            clearTimeout(this.pollingTimeout);
            this.pollingTimeout = null;
        }
    }

    private pruneEventNodeCache(currentEventIds: Set<string>): void {
        for (const [eventId, nodes] of this.nodesByEventId.entries()) {
            if (currentEventIds.has(eventId)) continue;

            // Event no longer exists in the latest poll results.
            const nodeIds = nodes?.map(n => n.event_node_id).filter(Boolean) ?? [];
            if (nodeIds.length) {
                this.mqttConnectionService.unsubscribe(nodeIds, 'EVENT_NODE');
            }
            this.nodesByEventId.delete(eventId);
        }
    }

    private pollEvents(): void {
        if (!this.pollingStarted) return;
        this.http.get<APIData<EventAPI[]>>(`${this.emsUrl}/v1/eps_events`, {
            params: {hierarchy: false},
            withCredentials: true
        }).pipe(
            retry(2)
        ).subscribe({
            next: (resp) => {
                if (!resp.data || !resp.data.length) {
                    // If there are no events at all, clear stale caches/subscriptions.
                    this.pruneEventNodeCache(new Set());
                    this.pruneSidebarExpandedEvents(new Set());

                    this.eventsAPI = [];
                    this.events = [];
                    this.events$.next([]);
                    this.scheduleNextPoll();
                    return;
                }

                // Always update our API cache so parseEvent uses latest values.
                const currentEventIds = new Set(resp.data.map(e => e.event_id));
                this.pruneEventNodeCache(currentEventIds);
                this.pruneSidebarExpandedEvents(currentEventIds);

                const hasNewEvents = resp.data.some(
                    newEvent => !this.eventsAPI.find(oldEvent => oldEvent.event_id === newEvent.event_id)
                );

                this.eventsAPI = resp.data;

                if (hasNewEvents) {
                    this.fetchProductsAndEmitEvents();
                } else {
                    // Emit per-event updates (for any listeners) AND emit the full list so getEvents() subscribers run every poll.
                    this.events = this.eventsAPI.map(api => this.parseEvent(api));
                    this.events.forEach((evt) => this.eventUpdate$.next(evt));
                    this.events$.next(this.events);
                }

                this.scheduleNextPoll();
            },
            error: (err) => {
                console.error('Error polling events:', err);
                this.scheduleNextPoll();
            }
        });
    }

    private scheduleNextPoll(): void {
        if (!this.pollingStarted) return;
        this.pollingTimeout = setTimeout(() => {
            this.pollEvents();
        }, 30000);
    }

    private fetchProductsAndEmitEvents(): void {
        const uniqueProductIds = [...new Set(this.eventsAPI.map(e => e.product_id).filter(id => id))];

        if (!uniqueProductIds.length) {
            this.parseAndEmitEvents();
            return;
        }

        const productRequests = uniqueProductIds.map(id => {
            if (this.productsMap.has(id)) {
                return of(this.productsMap.get(id)!);
            }
            return this.http.get<APIData<Product>>(`${this.emsUrl}/v1/product/${id}`, {withCredentials: true}).pipe(
                map(resp => resp.data),
                tap(product => this.productsMap.set(id, product)),
                catchError(err => {
                    console.error(`Error fetching product ${id}:`, err);
                    return of(null);
                })
            );
        });

        forkJoin(productRequests).subscribe(() => {
            this.parseAndEmitEvents();
        });
    }

    private parseAndEmitEvents(): void {
        this.events = this.eventsAPI.map(api => this.parseEvent(api));
        this.events$.next(this.events);
    }

    private parseEvent(api: EventAPI): Event {
        const locale = this.user ? this.user.preferences.defaultLocale : 'en_US';
        const product = this.productsMap.get(api.product_id);
        const progressStatus = this.getProgressStatus(api);

        return {
            ...api,
            event_progress_status: progressStatus,
            len_total_event_nodes: api.event_node_statistics.total_event_nodes,
            len_active_event_nodes: api.event_node_statistics.active_event_nodes,
            len_pending_event_nodes: api.event_node_statistics.pending_event_nodes,
            len_opted_out_event_nodes: api.event_node_statistics.opted_out_event_nodes,
            program: {
                id: api.program_id,
                name: parseLabel(api.program_name, locale),
            },
            product
        };
    }

    getEvents(): Observable<Event[]> {
        return this.events$;
    }

    numberFormat(input: number | null, fractionDigits: number, locale: string, isKw: Boolean): string | null {
        if (input === null) {
            return '?';
        }
        return Intl.NumberFormat(locale, {
            maximumFractionDigits: fractionDigits,
            minimumFractionDigits: fractionDigits
        }).format(isKw ? input : input/1000);
    }

    loadDropToTypeValue(product: ProductChart, eventNode: EventNodeAPI, perfValue: string, locale: string) {
        const productUOM = product.prez_conf.prefered_prez_demand_uom ?? eventNode.last_current_performance_uom;
        const performanceValueKW = Math.round(Math.abs(eventNode.last_current_target_value))
        perfValue = (productUOM.toLocaleLowerCase() === "kw"
            ? Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(performanceValueKW)
            : Intl.NumberFormat(locale, { maximumFractionDigits: 3, minimumFractionDigits: 3 }).format(performanceValueKW/1000));
        return perfValue;
    }

    keyPerfMetricValue = (locale: string, eventNode: EventNodeAPI, isKw: Boolean): any => {
        const keyValue = {
            [KeyPerformanceMetric.AVERAGE]: () => this.numberFormat(eventNode.average_performance_value, isKw ? 0 : 3, locale, isKw),
            [KeyPerformanceMetric.MAXIMUM]: () => this.numberFormat(eventNode.maximum_performance_value, isKw ? 0 : 3, locale, isKw),
            [KeyPerformanceMetric.MINIMUM]: () => this.numberFormat(eventNode.minimum_performance_value, isKw ? 0 : 3, locale, isKw)
        }
        return keyValue;
    }

    keyPerfMetricPercentage = (locale: string, eventNode: EventNodeAPI): any => {
        const keyValue = {
            [KeyPerformanceMetric.AVERAGE]: () => this.numberFormat(eventNode.average_performance_percentage, 0, locale, true),
            [KeyPerformanceMetric.MAXIMUM]: () => this.numberFormat(eventNode.maximum_performance_percentage, 0, locale, true),
            [KeyPerformanceMetric.MINIMUM]: () => this.numberFormat(eventNode.minimum_performance_percentage, 0, locale, true)
        }
        return keyValue;
    }

    getOverallPerformance(product: ProductChart, eventNode: EventNodeAPI, registration_type: string, locale: string) {
        const perfMetric = product?.key_performance_metric ?? KeyPerformanceMetric.AVERAGE;
        const isKw = (product?.prez_conf.prefered_prez_demand_uom ?? eventNode.last_current_performance_uom).toLocaleLowerCase() === 'kw';
        let perfValue: string = '';
        if (registration_type === RegistrationType.LOAD_DROP_TO) {
            return this.loadDropToTypeValue(product, eventNode, perfValue, locale);
        }
        return this.keyPerfMetricValue(locale, eventNode, isKw)[perfMetric]();
    }

    getOverallPercentage(product: ProductChart, eventNode: EventNodeAPI, registration_type: string, locale: string) {
        const perfMetric = product?.key_performance_metric ?? KeyPerformanceMetric.AVERAGE;
        if (registration_type === RegistrationType.LOAD_DROP_TO) {
            return Intl.NumberFormat(locale, {maximumFractionDigits: 0}).format(eventNode?.average_performance_percentage);
        }
        return this.keyPerfMetricPercentage(locale, eventNode)[perfMetric]();
    }

    getEventNodes(event_id: string, subscribeToRealtimeUpdates: boolean = true): Observable<EventNodeAPI[]> {
        if (this.nodesByEventId.has(event_id)) {
            console.log("Returning cached event nodes for event ID:", event_id);
            const cachedNodes = this.nodesByEventId.get(event_id)!;
            if (subscribeToRealtimeUpdates) {
                this.mqttConnectionService.subscribeToTopic(
                    cachedNodes.map(node => node.event_node_id),
                    'EVENT_NODE',
                    (msg) => this.handleEventNodeUpdate(event_id, msg)
                );
            }
            return of(cachedNodes);
        }

        console.log("Getting event nodes for event ID:", event_id);
        return this.http
            .get<APIData<EventNodeAPI[]>>(`${this.emsUrl}/v1/event/${event_id}/flat_node_list`, {
                params: {},
                withCredentials: true
            })
            .pipe(
                switchMap((response: APIData<EventNodeAPI[]>) => {
                    const siteRegistrationMap = new Map<string, Set<string>>();
                    response.data.forEach(item => {
                        if (!siteRegistrationMap.has(item.site_id)) {
                            siteRegistrationMap.set(item.site_id, new Set());
                        }
                        siteRegistrationMap.get(item.site_id)!.add(item.registration_id);
                    });
                    const eventNodes = response.data.map(item => {
                        const isEventAfter = moment().isAfter(moment(item.event_node_end_dttm_utc));
                        const perfPercentage = isEventAfter
                            ? item.average_performance_percentage ?? item.last_current_performance_percentage
                            : item.last_current_performance_percentage ?? item.average_performance_percentage;

                        return {
                            ...item,
                            perfPercentage,
                            site_to_registration_map: Array.from(siteRegistrationMap.get(item.site_id) ?? [])
                        };
                    });
                    const sortedEventNodes = sortBy(eventNodes, ['perfPercentage', 'site_name']);
                    const eventNodeObservables = sortedEventNodes.map(en =>
                        this.getRegistration(en.registration_id).pipe(
                            map(registration => ({
                                ...en,
                                registration
                            }))
                        )
                    );
                    
                    return forkJoin(eventNodeObservables);
                }),
                tap(eventNodes => {
                    this.nodesByEventId.set(event_id, eventNodes);

                    if (subscribeToRealtimeUpdates) {
                        const nodeIds = eventNodes.map(node => node.event_node_id);
                        this.mqttConnectionService.subscribeToTopic(
                            nodeIds,
                            'EVENT_NODE',
                            (msg) => this.handleEventNodeUpdate(event_id, msg)
                        );
                    }
                }),
                catchError(err => {
                    console.error('Error fetching event nodes:', err);
                    return of([]);
                })
            );
    }

    private handleEventNodeUpdate(event_id: string, updatedNode: EventNodeAPI): void {
        const nodes = this.nodesByEventId.get(event_id);
        if (!nodes) {
            console.log("MQTT | EventNodeUpdate: No cached nodes found for event ID:", event_id);
            return;
        }

        const index = nodes.findIndex(n => n.event_node_id === updatedNode.event_node_id);
        if (index !== -1) {
            nodes[index] = {
                ...nodes[index],
                ...updatedNode,
                perfPercentage: updatedNode.average_performance_percentage ?? updatedNode.last_current_performance_percentage
            };
            this.nodesByEventId.set(event_id, sortBy(nodes, ['perfPercentage', 'site_name']));
            this.eventNodeUpdate$.next(updatedNode);
        }
    }

    unsubscribeFromEventNodeUpdates(event_id: string): void {
        const nodes = this.nodesByEventId.get(event_id);
        if (!nodes?.length) {
            console.warn('MQTT | EventNodeUpdate: No cached nodes found to unsubscribe for event ID:', event_id);
            return;
        }

        const nodeIds = nodes.map(n => n.event_node_id);
        this.mqttConnectionService.unsubscribe(nodeIds, 'EVENT_NODE');
    }

    getProgressStatus(event: EventAPI): 'BEFORE' | 'DURING' | 'AFTER' {
        const startTime = event.event_node_statistics.start_dttm_utc;
        const endTime = event.event_node_statistics.end_dttm_utc;
        if (moment().utc().isBefore(moment(startTime).utc())) {
            return 'BEFORE';
        } else if (!endTime || moment().utc().isBefore(moment(endTime).utc())) {
            return 'DURING';
        } else {
            return 'AFTER';
        }
    }

    getRegistration(registration_id: string): Observable<Registration | null> {
        return this.http.get<Registration>(`${this.emsUrl}/v1/registration/${registration_id}`, {withCredentials: true}).pipe(
            timeout(10000),
            map((response: Registration) => {
                return response;
            }),
            catchError(err => {
                if (err.status === 404) {
                    console.warn(`Registration ${registration_id} not found.`);
                    return of(null);
                }
                if (err.name === 'TimeoutError') {
                    console.error('Request timed out');
                } else {
                    console.error('Unexpected error:', err);
                }
                return of(null);
            })
        );
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.mqttConnectionService.unsubscribeAll();
    }
}
